import { type FormEvent, useState } from "react";

import { ErrorNote } from "@/components/AppShell";
import NumericInput from "@/components/NumericInput";
import PostalCodeField from "@/components/PostalCodeField";
import { globalApi } from "@/lib/api";
import type { GeocodeResult, Vessel, VolumeEstimate } from "@/types";

interface Props {
  onComplete: () => void;
}

/**
 * First run. One scrolling form rather than a stepped wizard — it is eight
 * fields, done once, and paging through it would be more ceremony than it earns.
 *
 * The app seeds itself here so there is no hand-written migration for anyone to
 * maintain, and no blocked first launch waiting on data you may not have handy.
 */
export default function Setup({ onComplete }: Props) {
  const [name, setName] = useState("Backyard pool");
  const [location, setLocation] = useState("Crooks, SD");
  const [postalCode, setPostalCode] = useState("");
  const [place, setPlace] = useState<GeocodeResult | null>(null);
  const [waterSource, setWaterSource] = useState("Crooks Municipal Utilities");
  const [length, setLength] = useState("32");
  const [width, setWidth] = useState("16");
  const [shallow, setShallow] = useState("3");
  const [deep, setDeep] = useState("9");
  const [volume, setVolume] = useState("");
  const [chlorinePercent, setChlorinePercent] = useState("12.5");
  const [estimate, setEstimate] = useState<VolumeEstimate | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const canEstimate = [length, width, shallow, deep].every((v) => Number(v) > 0);

  async function estimateVolume() {
    setError(null);
    try {
      const result = await globalApi.post<VolumeEstimate>("/api/estimate-volume", {
        length_ft: length,
        width_ft: width,
        shallow_depth_ft: shallow,
        deep_depth_ft: deep,
      });
      setEstimate(result);
      setVolume(result.volume_gallons);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not work out the volume.");
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await globalApi.post<Vessel>("/api/setup", {
        vessel: {
          name,
          location: location || null,
          postal_code: place?.postal_code ?? (postalCode.trim() || null),
          // Only ever set together with the ZIP they were resolved from.
          latitude: place?.latitude ?? null,
          longitude: place?.longitude ?? null,
          ...(place?.timezone ? { timezone: place.timezone } : {}),
          water_source: waterSource || null,
          length_ft: length || null,
          width_ft: width || null,
          shallow_depth_ft: shallow || null,
          deep_depth_ft: deep || null,
          volume_gallons: volume,
          surface_type: "vinyl_liner",
          pool_type: "in_ground",
        },
        settings: { liquid_chlorine_percent: chlorinePercent },
      });
      onComplete();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save your pool.");
      setBusy(false);
    }
  }

  return (
    <main className="mx-auto min-h-dvh max-w-lg px-4 py-8">
      <h1 className="text-2xl font-semibold tracking-tight">Set up your pool</h1>
      <p className="mt-1 text-(--color-ink-muted)">
        A few details, once. Everything the app calculates depends on them.
      </p>

      <form onSubmit={submit} className="mt-6 space-y-4">
        <section className="card space-y-4 p-4">
          <Labelled label="Name" htmlFor="name">
            <input id="name" className="field" value={name} onChange={(e) => setName(e.target.value)} />
          </Labelled>
          {/* A label, and nothing more — it is what the header says, not where
              the app thinks the pool is. That is the ZIP below, and conflating
              the two is exactly how a pool ends up reading "Crooks, SD" while
              the overnight check reports no location set. */}
          <Labelled label="Location" htmlFor="location" hint="Just a label for the header.">
            <input
              id="location"
              className="field"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
            />
          </Labelled>
          <PostalCodeField
            value={postalCode}
            onChange={setPostalCode}
            resolved={place}
            onResolved={(next) => {
              setPlace(next);
              setPostalCode(next.postal_code);
            }}
            hasCoordinates={false}
          />
          <Labelled label="Water source" htmlFor="water-source">
            <input
              id="water-source"
              className="field"
              value={waterSource}
              onChange={(e) => setWaterSource(e.target.value)}
            />
          </Labelled>
        </section>

        <section className="card space-y-4 p-4">
          <h2 className="text-sm font-semibold">Size</h2>
          <div className="grid grid-cols-2 gap-3">
            <Labelled label="Length (ft)" htmlFor="length">
              <NumericInput
                id="length"
                className="field figure"
                value={length}
                onChange={setLength}
              />
            </Labelled>
            <Labelled label="Width (ft)" htmlFor="width">
              <NumericInput
                id="width"
                className="field figure"
                value={width}
                onChange={setWidth}
              />
            </Labelled>
            <Labelled label="Shallow end (ft)" htmlFor="shallow">
              <NumericInput
                id="shallow"
                className="field figure"
                value={shallow}
                onChange={setShallow}
              />
            </Labelled>
            <Labelled label="Deep end (ft)" htmlFor="deep">
              <NumericInput
                id="deep"
                className="field figure"
                value={deep}
                onChange={setDeep}
              />
            </Labelled>
          </div>

          <button
            type="button"
            onClick={estimateVolume}
            disabled={!canEstimate}
            className="btn btn-quiet w-full text-sm"
          >
            Work out the volume
          </button>
        </section>

        <section className="card space-y-3 p-4">
          <Labelled
            label="Volume (gallons)"
            htmlFor="volume"
            hint="Every dose the app suggests divides by this number. Use the real figure if you know it."
          >
            <NumericInput
              id="volume"
              className="field figure text-xl"
              value={volume}
              onChange={setVolume}
              required
            />
          </Labelled>
          {estimate && (
            <p className="text-xs text-(--color-ink-faint)">{estimate.note}</p>
          )}
        </section>

        <section className="card p-4">
          <Labelled
            label="Liquid chlorine strength (%)"
            htmlFor="chlorine"
            hint="10% and 12.5% are both common, and the difference changes every chlorine dose by about a quarter. Check the jug."
          >
            <NumericInput
              id="chlorine"
              className="field figure"
              value={chlorinePercent}
              onChange={setChlorinePercent}
            />
          </Labelled>
        </section>

        {error && <ErrorNote message={error} />}

        <button type="submit" disabled={busy || !volume} className="btn btn-primary w-full">
          {busy ? "Saving…" : "Start tracking"}
        </button>
      </form>
    </main>
  );
}

function Labelled({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      <div className="mt-1.5">{children}</div>
      {hint && <p className="mt-1.5 text-xs text-(--color-ink-faint)">{hint}</p>}
    </div>
  );
}
